import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Area,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NumericFormat as NumberFormat } from "react-number-format";
import { NavBarLateral } from "./NavBarLateral";
import { dashboardService } from "../../_services";
import alertService from "../../_services/cart/alert.service";
import { ESTADOS_VENTA } from "./ventas/utils/estadosVenta";

/**
 * El panel del vendedor: cómo viene el negocio.
 *
 * <h3>Ordenada por lo que se hace con el dato, no por lo que se midió</h3>
 * Arriba van los números del período con su comparación contra el anterior —que es lo único que
 * responde "¿esto está bien o mal?"—, después la evolución, después de dónde sale la plata (qué
 * repuestos, qué marcas), y al final lo que está trabado: ventas paradas en un estado, consultas sin
 * contestar, publicaciones sin stock. Lo accionable primero y último; el contexto en el medio.
 *
 * <h3>Cada número viene con su variación</h3>
 * "$840.000 facturado" suelto no dice nada. "$840.000, 23% más que el mes pasado" sí. Por eso el back
 * devuelve todo dos veces —la ventana elegida y la anterior del mismo largo— y acá se pinta la flecha.
 * Cuando antes no había nada la variación viene en null y no se dibuja flecha: de cero a diez no es
 * "un 1000% más", es la primera vez.
 *
 * <h3>La serie siempre son doce meses</h3>
 * Aunque el selector esté en 30 días. El selector mueve los números gruesos y los rankings; una serie
 * temporal de treinta días no es una serie, son cuatro puntos que no muestran ninguna estacionalidad.
 */

/**
 * Colores de los gráficos.
 *
 * <p>Valores fijos y no variables del tema, por la misma razón que en la pantalla de demanda: el
 * sitio invierte la escala de grises entre claro y oscuro, y una serie que cambia de color al cambiar
 * el tema deja de ser comparable con lo que la persona vio hace un minuto. Todos rondan el 50% de
 * luminosidad, que es lo que los hace legibles sobre los dos fondos.
 */
const PALETA = [
    "hsl(18, 100%, 50%)",
    "hsl(216, 75%, 55%)",
    "hsl(174, 62%, 42%)",
    "hsl(266, 60%, 60%)",
    "hsl(142, 55%, 45%)",
    "hsl(330, 70%, 60%)",
    "hsl(42, 95%, 52%)",
    "hsl(196, 80%, 48%)",
];

const VENTANAS = [
    { dias: 30, label: "30 días" },
    { dias: 90, label: "90 días" },
    { dias: 365, label: "12 meses" },
];

const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/** "2026-09" → "sep 26". El eje tiene que entrar en el ancho de un teléfono. */
const rotularPeriodo = (periodo) => {
    if (!periodo || periodo.length < 7) return periodo || "";
    return `${MESES_CORTOS[parseInt(periodo.slice(5, 7), 10) - 1] || periodo.slice(5, 7)} ${periodo.slice(2, 4)}`;
};

/** Recorta nombres largos para el eje sin tocar el tooltip, que muestra el nombre completo. */
const acortar = (texto, max) => {
    if (!texto) return "";
    return texto.length > max ? `${texto.slice(0, max - 1)}…` : texto;
};

/** "2026-09-30" → "30/09". Se parte el string: `new Date` de una fecha ISO corre un día en UTC-3. */
const fechaCorta = (fecha) => {
    if (!fecha) return "";
    const partes = String(fecha).split("-");
    return partes.length === 3 ? `${partes[2]}/${partes[1]}` : fecha;
};

const Monto = ({ value, decimales = 0 }) => (
    <NumberFormat
        prefix={"$"}
        value={value ?? 0}
        displayType={"text"}
        thousandSeparator="."
        decimalSeparator=","
        decimalScale={decimales}
        fixedDecimalScale={decimales > 0}
    />
);

/**
 * La flecha de variación.
 *
 * <p>`null` no es cero: significa que en el período anterior no hubo nada con qué comparar, y ahí no
 * se dibuja nada. Un "+∞%" o un "+1000%" en la primera venta del mes es ruido, no información.
 *
 * <p>`invertido` es para las métricas donde bajar es bueno — hoy ninguna lo usa, pero el día que se
 * agregue "tiempo de respuesta" la flecha verde tiene que apuntar para abajo.
 */
const Variacion = ({ valor, invertido = false }) => {
    if (valor === null || valor === undefined) return null;

    const sube = Number(valor) > 0;
    const plano = Number(valor) === 0;
    const bien = invertido ? !sube : sube;

    return (
        <span
            className={`panel__variacion ${
                plano ? "panel__variacion--plana" : bien ? "panel__variacion--bien" : "panel__variacion--mal"
            }`}
        >
            {!plano && <FontAwesomeIcon icon={sube ? "fa-solid fa-arrow-trend-up" : "fa-solid fa-arrow-trend-down"} />}
            {sube ? "+" : ""}
            {valor}%
        </span>
    );
};

/** Un número grande con su etiqueta. `destacado` es para el único dato accionable del bloque. */
const Tile = ({ valor, etiqueta, detalle, variacion, invertido, destacado, alerta }) => (
    <div
        className={`panel__tile${destacado ? " panel__tile--destacado" : ""}${alerta ? " panel__tile--alerta" : ""}`}
    >
        <span className="panel__tile-valor">{valor}</span>
        <span className="panel__tile-etiqueta">
            {etiqueta}
            <Variacion valor={variacion} invertido={invertido} />
        </span>
        {detalle && <span className="panel__tile-detalle">{detalle}</span>}
    </div>
);

/**
 * El tooltip de los gráficos.
 *
 * <p>Propio y no el de recharts porque el de fábrica hereda el fondo del contenedor: sobre el tema
 * oscuro queda texto gris sobre gris. Este pinta con las variables del sitio, que ya están definidas
 * para los dos temas.
 */
const TooltipPanel = ({ active, payload, label, formato = {} }) => {
    if (!active || !payload || payload.length === 0) return null;

    const fila = payload[0].payload;

    return (
        <div className="panel__tooltip">
            <strong>{fila.nombreCompleto || label}</strong>
            {payload.map((serie) => (
                <span key={serie.dataKey} style={{ color: serie.color }}>
                    {serie.name}:{" "}
                    {formato[serie.dataKey] === "money" ? <Monto value={serie.value} /> : serie.value}
                </span>
            ))}
        </div>
    );
};

const Dashboard = () => {
    const [panel, setPanel] = useState(undefined);
    const [cargando, setCargando] = useState(true);
    const [dias, setDias] = useState(30);

    useEffect(() => {
        let cancelado = false;
        setCargando(true);

        dashboardService
            .traerDashboard(dias)
            .then((data) => {
                if (!cancelado) setPanel(data);
            })
            .catch((error) => {
                if (!cancelado) alertService.error(error);
            })
            .finally(() => {
                if (!cancelado) setCargando(false);
            });

        return () => {
            cancelado = true;
        };
    }, [dias]);

    const kpis = panel?.kpis;
    const ofertas = panel?.ofertas;

    const serie = useMemo(
        () => (panel?.serie || []).map((m) => ({ ...m, etiqueta: rotularPeriodo(m.periodo) })),
        [panel]
    );

    const topProductos = useMemo(
        () =>
            (panel?.topProductos || []).map((p) => ({
                ...p,
                nombreCompleto: [p.nombre, p.detalle].filter(Boolean).join(" · "),
                etiqueta: acortar(p.nombre, 20),
            })),
        [panel]
    );

    const topMarcas = useMemo(
        () =>
            (panel?.topMarcas || []).map((m) => ({
                ...m,
                nombreCompleto: m.nombre,
                etiqueta: acortar(m.nombre, 16),
            })),
        [panel]
    );

    /*
     * El embudo se dibuja con el mismo orden, colores e iconos que las tarjetas del panel de ventas
     * (ESTADOS_VENTA): es la misma información mirada desde otra pantalla, y que "Pendiente de pago"
     * fuera amarillo acá y gris allá obligaría a releer la leyenda cada vez.
     */
    const embudo = useMemo(() => {
        const porEstado = new Map((panel?.porEstado || []).map((e) => [e.estado, e]));

        return ESTADOS_VENTA.map((estado) => ({
            ...estado,
            cantidad: porEstado.get(estado.key)?.cantidad || 0,
            monto: porEstado.get(estado.key)?.monto || 0,
        }));
    }, [panel]);

    const sinMovimiento =
        !cargando && kpis && kpis.ventas === 0 && kpis.consultas === 0 && kpis.publicacionesActivas === 0;

    return (
        <div className="dashboard">
            <NavBarLateral handleClose={() => {}} />

            <div className="dashboard__content">
                <div className="dashboard__content-header">
                    <div className="dashboard__content-title">
                        <h2>Estadísticas</h2>
                    </div>

                    <div className="panel__ventanas">
                        {VENTANAS.map((v) => (
                            <button
                                key={v.dias}
                                type="button"
                                className={v.dias === dias ? "panel__ventana panel__ventana--activa" : "panel__ventana"}
                                onClick={() => setDias(v.dias)}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                </div>

                <p className="panel__bajada">
                    Cómo viene tu negocio. Los números de arriba son de los últimos{" "}
                    {dias === 365 ? "12 meses" : `${dias} días`} y se comparan contra el período anterior del mismo
                    largo.
                </p>

                {cargando && <p className="panel__estado-carga">Cargando…</p>}

                {sinMovimiento && (
                    <div className="panel__vacio">
                        <FontAwesomeIcon icon="fa-solid fa-chart-line" size="2x" />
                        <p>Todavía no hay movimiento para mostrar.</p>
                        <span>
                            Cuando publiques repuestos y empieces a recibir consultas y ventas, acá vas a ver cómo
                            viene todo.
                        </span>
                        <Link className="button" data-type="primary" to="/vendedor/addEditProducto">
                            Publicar un repuesto
                        </Link>
                    </div>
                )}

                {!cargando && kpis && !sinMovimiento && (
                    <>
                        <section className="panel__tiles">
                            <Tile
                                valor={<Monto value={kpis.facturado} />}
                                etiqueta="Facturado"
                                detalle={
                                    <>
                                        Período anterior: <Monto value={kpis.facturadoPrevio} />
                                    </>
                                }
                                variacion={kpis.facturadoVariacion}
                                destacado
                            />
                            <Tile
                                valor={kpis.ventas}
                                etiqueta="Ventas cerradas"
                                detalle={`${kpis.unidades} ${kpis.unidades === 1 ? "unidad" : "unidades"}`}
                                variacion={kpis.ventasVariacion}
                            />
                            <Tile
                                valor={<Monto value={kpis.ticketPromedio} />}
                                etiqueta="Ticket promedio"
                                detalle="Por venta cerrada"
                            />
                            <Tile
                                valor={kpis.consultas}
                                etiqueta="Consultas recibidas"
                                detalle={
                                    kpis.consultasSinResponder > 0
                                        ? `${kpis.consultasSinResponder} sin responder`
                                        : "Todas respondidas"
                                }
                                variacion={kpis.consultasVariacion}
                                alerta={kpis.consultasSinResponder > 0}
                            />
                            <Tile
                                valor={kpis.conversion === null ? "—" : `${kpis.conversion}%`}
                                etiqueta="Conversión"
                                detalle="De cada 100 que preguntan, cuántos compran"
                            />
                            <Tile
                                valor={<Monto value={kpis.descuentoOtorgado} />}
                                etiqueta="Descuentos otorgados"
                                detalle="Lo que resignaste en ofertas"
                            />
                        </section>

                        <section className="panel__card panel__card--ancha">
                            <header className="panel__card-head">
                                <h3>Cómo viene el año</h3>
                                <span className="panel__card-nota">
                                    Siempre 12 meses, sin importar el período elegido arriba
                                </span>
                            </header>

                            {/*
                                Facturación y consultas en el mismo gráfico, con dos ejes: son las dos puntas
                                del mismo embudo. Separados en dos tarjetas, que un pico de consultas no se
                                haya traducido en ventas —el dato más útil de la pantalla— había que buscarlo
                                comparando dos imágenes.
                            */}
                            <div className="panel__chart">
                                <ResponsiveContainer width="100%" height={260}>
                                    <ComposedChart data={serie} margin={{ left: 4, right: 8, top: 8 }}>
                                        <defs>
                                            <linearGradient id="gradPanel" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={PALETA[0]} stopOpacity={0.45} />
                                                <stop offset="100%" stopColor={PALETA[0]} stopOpacity={0.02} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid vertical={false} stroke="var(--neutral-200, #d2d2d2)" />
                                        <XAxis
                                            dataKey="etiqueta"
                                            stroke="var(--neutral-500, #808080)"
                                            fontSize={12}
                                            interval="preserveStartEnd"
                                            minTickGap={16}
                                        />
                                        <YAxis
                                            yAxisId="plata"
                                            stroke="var(--neutral-500, #808080)"
                                            fontSize={11}
                                            width={54}
                                            tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
                                        />
                                        <YAxis
                                            yAxisId="cuenta"
                                            orientation="right"
                                            allowDecimals={false}
                                            stroke="var(--neutral-500, #808080)"
                                            fontSize={11}
                                            width={28}
                                        />
                                        <Tooltip content={<TooltipPanel formato={{ facturado: "money" }} />} />
                                        <Area
                                            yAxisId="plata"
                                            type="monotone"
                                            dataKey="facturado"
                                            name="Facturado"
                                            stroke={PALETA[0]}
                                            strokeWidth={2}
                                            fill="url(#gradPanel)"
                                        />
                                        <Line
                                            yAxisId="cuenta"
                                            type="monotone"
                                            dataKey="consultas"
                                            name="Consultas"
                                            stroke={PALETA[1]}
                                            strokeWidth={2}
                                            dot={false}
                                        />
                                        <Line
                                            yAxisId="cuenta"
                                            type="monotone"
                                            dataKey="ventas"
                                            name="Ventas"
                                            stroke={PALETA[4]}
                                            strokeWidth={2}
                                            dot={false}
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>

                            <ul className="panel__leyenda">
                                <li style={{ "--color": PALETA[0] }}>Facturado</li>
                                <li style={{ "--color": PALETA[1] }}>Consultas</li>
                                <li style={{ "--color": PALETA[4] }}>Ventas</li>
                            </ul>
                        </section>

                        <section className="panel__graficos">
                            <article className="panel__card">
                                <h3>Qué se vende más</h3>

                                {topProductos.length === 0 ? (
                                    <p className="panel__sin-datos">
                                        Todavía no vendiste nada en este período.
                                    </p>
                                ) : (
                                    <div className="panel__chart">
                                        <ResponsiveContainer
                                            width="100%"
                                            height={Math.max(180, topProductos.length * 36)}
                                        >
                                            <BarChart
                                                data={topProductos}
                                                layout="vertical"
                                                margin={{ left: 4, right: 16 }}
                                            >
                                                <CartesianGrid
                                                    horizontal={false}
                                                    stroke="var(--neutral-200, #d2d2d2)"
                                                />
                                                <XAxis
                                                    type="number"
                                                    stroke="var(--neutral-500, #808080)"
                                                    fontSize={11}
                                                    tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
                                                />
                                                <YAxis
                                                    type="category"
                                                    dataKey="etiqueta"
                                                    width={110}
                                                    stroke="var(--neutral-500, #808080)"
                                                    fontSize={12}
                                                />
                                                <Tooltip
                                                    cursor={{ fill: "var(--neutral-100, #f2f2f2)" }}
                                                    content={<TooltipPanel formato={{ facturado: "money" }} />}
                                                />
                                                <Bar dataKey="facturado" name="Facturado" radius={[0, 4, 4, 0]}>
                                                    {topProductos.map((p, i) => (
                                                        <Cell key={p.id ?? i} fill={PALETA[i % PALETA.length]} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </article>

                            <article className="panel__card">
                                <h3>Qué marcas te dan de comer</h3>

                                {topMarcas.length === 0 ? (
                                    <p className="panel__sin-datos">
                                        Todavía no vendiste nada en este período.
                                    </p>
                                ) : (
                                    <div className="panel__chart">
                                        <ResponsiveContainer
                                            width="100%"
                                            height={Math.max(180, topMarcas.length * 36)}
                                        >
                                            <BarChart data={topMarcas} layout="vertical" margin={{ left: 4, right: 16 }}>
                                                <CartesianGrid
                                                    horizontal={false}
                                                    stroke="var(--neutral-200, #d2d2d2)"
                                                />
                                                <XAxis
                                                    type="number"
                                                    stroke="var(--neutral-500, #808080)"
                                                    fontSize={11}
                                                    tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
                                                />
                                                <YAxis
                                                    type="category"
                                                    dataKey="etiqueta"
                                                    width={92}
                                                    stroke="var(--neutral-500, #808080)"
                                                    fontSize={12}
                                                />
                                                <Tooltip
                                                    cursor={{ fill: "var(--neutral-100, #f2f2f2)" }}
                                                    content={<TooltipPanel formato={{ facturado: "money" }} />}
                                                />
                                                <Bar dataKey="facturado" name="Facturado" radius={[0, 4, 4, 0]}>
                                                    {topMarcas.map((m, i) => (
                                                        <Cell key={m.id ?? i} fill={PALETA[i % PALETA.length]} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </article>
                        </section>

                        {/*
                            El embudo. No es un gráfico: son cinco números que se leen de un vistazo y que
                            llevan a la pantalla de ventas ya filtrada, que es lo que se quiere hacer después
                            de ver que hay tres pedidos esperando despacho.
                        */}
                        <section className="panel__card">
                            <header className="panel__card-head">
                                <h3>En qué estado están tus ventas</h3>
                                <Link className="panel__card-link" to="/vendedor/ventas">
                                    Ver todas <FontAwesomeIcon icon="fa-solid fa-arrow-right" />
                                </Link>
                            </header>

                            <div className="panel__embudo">
                                {embudo.map((estado) => (
                                    <Link
                                        key={estado.key}
                                        className="panel__embudo-item"
                                        data-tono={estado.tono}
                                        to="/vendedor/ventas"
                                        title={estado.ayuda}
                                    >
                                        <span className="panel__embudo-icono">
                                            <FontAwesomeIcon icon={estado.icono} />
                                        </span>
                                        <span className="panel__embudo-titulo">{estado.titulo}</span>
                                        <span className="panel__embudo-cantidad">{estado.cantidad}</span>
                                        <span className="panel__embudo-monto">
                                            <Monto value={estado.monto} />
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </section>

                        <section className="panel__graficos">
                            {/*
                                Ofertas e inventario van juntos y al final: los dos hablan de lo que hay
                                puesto hoy, no de lo que pasó. Es también donde se decide qué tocar mañana
                                —renovar una promo que vence, reponer lo que se quedó sin stock—, así que
                                cierran la pantalla con el link a la pantalla donde se hace.
                            */}
                            <article className="panel__card">
                                <header className="panel__card-head">
                                    <h3>Tus ofertas</h3>
                                    <Link className="panel__card-link" to="/vendedor/ofertas">
                                        Administrar <FontAwesomeIcon icon="fa-solid fa-arrow-right" />
                                    </Link>
                                </header>

                                <div className="panel__mini-tiles">
                                    <div className="panel__mini-tile">
                                        <span>{ofertas?.vigentes || 0}</span>
                                        <small>Corriendo ahora</small>
                                    </div>
                                    <div className="panel__mini-tile">
                                        <span>{ofertas?.programadas || 0}</span>
                                        <small>Programadas</small>
                                    </div>
                                    <div className="panel__mini-tile">
                                        <span>
                                            <Monto value={kpis.descuentoOtorgado} />
                                        </span>
                                        <small>Resignado en el período</small>
                                    </div>
                                </div>

                                {ofertas?.porVencer?.length > 0 && (
                                    <div className="panel__aviso">
                                        <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" />
                                        <div>
                                            <strong>Se vencen esta semana</strong>
                                            <ul>
                                                {ofertas.porVencer.map((o) => (
                                                    <li key={o.id}>
                                                        {o.nombre} — hasta el {fechaCorta(o.hasta)}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}

                                {ofertas?.rendimiento?.length > 0 ? (
                                    <table className="panel__tabla">
                                        <thead>
                                            <tr>
                                                <th>Oferta</th>
                                                <th>Unid.</th>
                                                <th>Facturado</th>
                                                <th>Descuento</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ofertas.rendimiento.map((o) => (
                                                <tr key={o.id}>
                                                    <td>{o.nombre}</td>
                                                    <td className="panel__tabla-num">{o.unidades}</td>
                                                    <td className="panel__tabla-num">
                                                        <Monto value={o.facturado} />
                                                    </td>
                                                    <td className="panel__tabla-num panel__tabla-num--resta">
                                                        <Monto value={o.descuento} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p className="panel__sin-datos">
                                        Ninguna venta del período se cerró con una oferta.{" "}
                                        <Link to="/vendedor/ofertas">Crear una</Link> es la forma más rápida de mover
                                        lo que está parado.
                                    </p>
                                )}
                            </article>

                            <article className="panel__card">
                                <header className="panel__card-head">
                                    <h3>Tu catálogo</h3>
                                    <Link className="panel__card-link" to="/vendedor/productos">
                                        Ver publicaciones <FontAwesomeIcon icon="fa-solid fa-arrow-right" />
                                    </Link>
                                </header>

                                <div className="panel__mini-tiles">
                                    <div className="panel__mini-tile">
                                        <span>{kpis.publicacionesActivas}</span>
                                        <small>Activas</small>
                                    </div>
                                    <div className="panel__mini-tile">
                                        <span>{kpis.publicacionesPausadas}</span>
                                        <small>Pausadas</small>
                                    </div>
                                    <div
                                        className={
                                            kpis.publicacionesSinStock > 0
                                                ? "panel__mini-tile panel__mini-tile--alerta"
                                                : "panel__mini-tile"
                                        }
                                    >
                                        <span>{kpis.publicacionesSinStock}</span>
                                        <small>Sin stock</small>
                                    </div>
                                </div>

                                <p className="panel__inventario">
                                    <span className="panel__inventario-label">Valor de lo publicado</span>
                                    <strong>
                                        <Monto value={kpis.valorInventario} />
                                    </strong>
                                    <small>
                                        Es el capital que tenés parado a precio de lista. Comparado contra lo que
                                        facturaste, dice qué tan rápido rota.
                                    </small>
                                </p>
                            </article>
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};

export { Dashboard };
